import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import AnimatedCross from "@/components/AnimatedCross";
import Ticker from "@/components/Ticker";
import HeroCarousel from "@/components/HeroCarousel";
import ProductCard from "@/components/ProductCard";
import VisitorCounter from "@/components/VisitorCounter";
import { api, fileUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const AUDIENCE_FILTERS = [
  { value: "", label: "Tous" },
  { value: "homme", label: "Homme" },
  { value: "femme", label: "Femme" },
  { value: "enfant", label: "Enfant" },
];

const CATEGORY_FILTERS = [
  { value: "", label: "Toutes catégories" },
  { value: "chaines", label: "Chaînes" },
  { value: "bracelets", label: "Bracelets" },
  { value: "boucles", label: "Boucles" },
  { value: "ensembles", label: "Ensembles" },
  { value: "chaussures", label: "Chaussures" },
  { value: "accessoires", label: "Accessoires" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [audience, setAudience] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    // Track visit (once per tab session)
    if (!sessionStorage.getItem("rdf_visit_tracked")) {
      api.post("/visits").then(() => sessionStorage.setItem("rdf_visit_tracked", "1")).catch(() => {});
    }
  }, []);

  const filtered = products.filter((p) => {
    if (audience && p.audience !== audience) return false;
    if (category && p.category !== category) return false;
    return true;
  });

  return (
    <div>
      {/* INTRO VIDEO — right below the header logo */}
      {settings?.intro_video_path && (
        <section className="w-full bg-black" data-testid="intro-video-section">
          <div className="max-w-7xl mx-auto">
            <video
              src={fileUrl(settings.intro_video_path)}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-auto max-h-[420px] object-cover"
              data-testid="intro-video"
            />
          </div>
        </section>
      )}

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="absolute inset-0 flex items-center justify-end pr-4 lg:pr-0 opacity-90">
          <AnimatedCross size={520} className="hidden md:block" />
        </div>
        <div className="absolute md:hidden inset-0 flex items-center justify-center opacity-25">
          <AnimatedCross size={360} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <div className="label-tiny mb-6 flex items-center gap-3">
              <span className="gold-line" /> {`Bijoux Chrétiens d'Exception`}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-[#1A1A1A] leading-[1.05] tracking-tight">
              Là où la grâce <br />
              devient <span className="gold-shimmer">parure</span>.
            </h1>
            <p className="mt-8 font-citation italic text-lg lg:text-xl text-[#1A1A1A]/80 leading-relaxed max-w-xl">
              {`"Car je n'ai point honte de l'Évangile : c'est une puissance de Dieu pour le salut de quiconque croit."`}
              <span className="block mt-2 not-italic text-xs tracking-[0.3em] text-[#B8860B]">— ROMAINS 1:16</span>
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/catalogue" className="btn-primary" data-testid="hero-cta-btn">
                Découvrir la Collection
              </Link>
              <a href="#temoignages" className="btn-secondary">
                Témoignages
              </a>
            </div>
            <div className="mt-8">
              <VisitorCounter />
            </div>
          </motion.div>
        </div>
      </section>

      {/* HERO CAROUSEL */}
      <HeroCarousel products={products} />

      {/* TICKER */}
      <Ticker />

      {/* FILTERS + CATALOG */}
      <section id="catalogue" className="py-20 lg:py-28 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="label-tiny mb-3 flex items-center gap-3">
              <span className="gold-line" /> Notre Collection
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Pièces sélectionnées <br className="hidden sm:inline" />
              <span className="text-[#B8860B]">avec foi</span>
            </h2>
          </div>
          <Link to="/catalogue" className="text-xs uppercase tracking-[0.25em] text-[#B8860B] hover:text-[#FFE9A6]">
            Voir tout le catalogue →
          </Link>
        </div>

        {/* Audience filter */}
        <div className="mb-4 flex flex-wrap gap-2">
          {AUDIENCE_FILTERS.map((f) => (
            <button
              key={f.value}
              data-testid={`filter-audience-${f.value || "all"}`}
              onClick={() => setAudience(f.value)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition ${
                audience === f.value
                  ? "bg-[#B8860B] text-[#0A0A0A] border-[#B8860B]"
                  : "bg-transparent text-[#1A1A1A]/75 border-black/10 hover:border-[#B8860B]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Category filter */}
        <div className="mb-10 flex flex-wrap gap-2">
          {CATEGORY_FILTERS.map((f) => (
            <button
              key={f.value}
              data-testid={`filter-category-${f.value || "all"}`}
              onClick={() => setCategory(f.value)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border transition ${
                category === f.value
                  ? "bg-[#B8860B]/10 text-[#B8860B] border-[#B8860B]"
                  : "bg-transparent text-[#6B6B6B] border-black/5 hover:text-[#1A1A1A]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-[#6B6B6B] py-20">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-black/5">
            <div className="text-5xl text-[#B8860B]/40 mb-4 font-display">✟</div>
            <p className="text-[#6B6B6B]">Aucun produit dans cette sélection pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-testid="home-product-grid">
            {filtered.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* VERSE BANNER */}
      <section className="relative py-24 lg:py-36 border-y border-black/5 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1490730141103-6cac27aaab94?auto=format&fit=crop&q=80")' }}
        />
        <div className="absolute inset-0 bg-[#FAF7F0]/85" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="font-citation italic text-2xl sm:text-3xl lg:text-4xl text-[#1A1A1A] leading-relaxed">
            {`"Car je n'ai point honte de l'Évangile : c'est une puissance de Dieu pour le salut de quiconque croit."`}
          </p>
          <p className="mt-8 text-xs tracking-[0.4em] uppercase text-[#B8860B]">— Romains 1:16</p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section id="temoignages" className="py-20 lg:py-28 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="label-tiny mb-3 flex items-center gap-3">
              <span className="gold-line" /> Témoignages
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Paroles de nos <span className="text-[#B8860B]">fidèles</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((t) => (
              <div key={t.id} className="border border-black/5 bg-white overflow-hidden flex flex-col" data-testid={`testimonial-${t.id}`}>
                {t.photo && (
                  <div className="aspect-video w-full overflow-hidden bg-[#FAF7F0] border-b border-[#B8860B]/20">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${t.photo}`}
                      alt={t.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-7 flex-1 flex flex-col">
                  <div className="text-[#B8860B] mb-3 text-sm tracking-widest">
                    {"★".repeat(t.rating)}{"☆".repeat(5 - t.rating)}
                  </div>
                  <p className="font-citation italic text-[#1A1A1A]/85 leading-relaxed flex-1">{`"${t.review}"`}</p>
                  <div className="mt-5 text-xs uppercase tracking-[0.2em] text-[#B8860B]">— {t.name}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
